import {
  ExcludeDescendants,
  KnowledgeGraphManagerApi,
  NodesResolveRequest,
} from '@agilelab/plugin-wb-marketplace-common';
import {
  AdaptedEntity,
  GenericEntityType,
  parseDisplayField,
  Provider,
  ProviderFilters,
  ReturnFieldEnum,
  selectTemplateEntity,
  TemplateEntity,
  TemplateColumn,
  TextFieldValue,
  useLoaderNunjucks,
  WbAlert,
  WbSafeNunjucksApi,
} from '@agilelab/plugin-wb-platform';
import { FC, ReactNode, useMemo } from 'react';
import {
  containsNunjucksExpression,
  SkosOptions,
  translateSkosToRdf,
} from './skosPreset';
import {
  RdfConceptsHandlerProvider,
  useRdfConceptsContext,
} from '../RdfConceptsProvider/RdfConceptsProvider';

export const BUSINESS_CONCEPT_TYPE = 'BusinessConcept';

/** Default SKOS column when the template author does not specify any. */
const SKOS_DEFAULT_COLUMN: TemplateColumn = {
  name: 'Label',
  path: 'skos:prefLabel',
};

/**
 * Extracts SKOS options from a TemplateEntity so they can be forwarded to
 * `translateSkosToRdf`. Template authors place these fields at the same level
 * as the other `entities[i]` properties:
 *
 * ```yaml
 * entities:
 *   - type: BusinessConcept
 *     displayName: Business Concept
 *     preset: skos
 *     conceptSchemeFilter: ['BankingVocabulary']
 *     displayField: skos:prefLabel
 *     columns:
 *       - name: Label
 *         path: skos:prefLabel
 * ```
 */
function extractSkosOptions(templateKind: TemplateEntity): SkosOptions {
  return {
    preset: 'skos',
    conceptSchemeFilter: templateKind.conceptSchemeFilter as
      | string[]
      | undefined,
    conceptFilter: templateKind.conceptFilter as string[] | undefined,
    expandStartNodes: templateKind.expandStartNodes as boolean | undefined,
    // displayField carries the RDF predicate name used as the display label
    // (e.g. "skos:prefLabel"). Follows the same TemplateEntity idiom as other
    // providers; defaults to "skos:prefLabel" inside translateSkosToRdf when absent.
    labelProperty: templateKind.displayField,
    // allowMultipleSelections and outputFormat are not used in the provider
    // context — they are handled by EntitySearchPicker (multiSelection) and
    // the TemplateEntity.returnField property respectively.
    allowMultipleSelections: undefined,
    outputFormat: undefined,
    columns: templateKind.columns as TemplateColumn[] | undefined,
    relations: templateKind.relations as string[] | undefined,
    inverseRelations: templateKind.inverseRelations as string[] | undefined,
    schemeRelations: templateKind.schemeRelations as string[] | undefined,
    schemeInverseRelations: templateKind.schemeInverseRelations as
      | string[]
      | undefined,
    excludeDescendants: templateKind.excludeDescendants as
      | ExcludeDescendants
      | undefined,
    childrenPageSize: templateKind.childrenPageSize as number | undefined,
  };
}

interface DynamicSkosFilterResolverProps {
  kgmApi: KnowledgeGraphManagerApi;
  nunjucksApi: WbSafeNunjucksApi;
  skosOptions: SkosOptions;
  formContext: any;
  templateKind: TemplateEntity;
  defaultColumns: TemplateColumn[];
  children: ReactNode;
}

/**
 * Result of resolving a `conceptSchemeFilter` / `conceptFilter` array against
 * `formContext`:
 * - `values`: successfully rendered, non-empty entries — used as the actual filter.
 * - `errors`: entries whose Nunjucks expression threw while rendering (as
 *   opposed to rendering to an empty string, which is a valid "not filled in
 *   yet" state and is silently excluded from `values`).
 */
interface ResolvedFilterEntries {
  values: string[];
  errors: string[];
}

/**
 * Renders each filter entry against `formContext`. An entry that renders to
 * an empty string (e.g. a Nunjucks expression depending on a form field that
 * hasn't been filled in yet, such as `{{ domain.spec.iri }}`) is excluded
 * from the result so the filter behaves as if it were never configured —
 * the picker then falls back to its "no filter" default (all concept
 * schemes / all concepts). An entry that fails to render (an actual Nunjucks
 * error, e.g. invalid syntax or an unknown filter) is reported in `errors`.
 */
export function resolveSkosFilterEntries(
  nunjucksApi: WbSafeNunjucksApi,
  formContext: any,
  entries: string[],
): ResolvedFilterEntries {
  const values: string[] = [];
  const errors: string[] = [];
  for (const entry of entries) {
    const rendered = parseDisplayField(nunjucksApi, formContext, entry);
    if (rendered === undefined) {
      errors.push(entry);
    } else if (rendered !== '') {
      values.push(rendered);
    }
  }
  return { values, errors };
}

/**
 * Resolves `conceptSchemeFilter` / `conceptFilter` entries that contain
 * Nunjucks expressions (e.g. `{{ domain.spec.iri }}`)
 * against the current `formContext`, then builds a fresh
 * `RdfConceptsHandlerProvider` whose `searchTreeParams` reflect the resolved
 * values. Re-renders (and re-fetches) whenever `formContext` changes, so
 * filters can react to other form fields (e.g. a Domain picker filled in
 * earlier in the template).
 *
 * - Entries that render to an empty string (dependent field not filled in
 *   yet) are excluded from the filter, so the picker falls back to "no
 *   filter configured" (all concept schemes / all concepts) instead of
 *   showing no results.
 * - Entries that fail to render (a genuine Nunjucks error) are reported as an
 *   inline `WbAlert` rendered above the (empty) results inside the picker
 *   dialog. This can't be surfaced via `EntitySearchPicker`'s field-level
 *   error UI because the resolver (and thus the error) only exists while the
 *   dialog is mounted, i.e. while the dialog is open.
 */
const DynamicSkosFilterResolver: FC<DynamicSkosFilterResolverProps> = ({
  kgmApi,
  nunjucksApi,
  skosOptions,
  formContext,
  templateKind,
  defaultColumns,
  children,
}) => {
  const templatesToLoad = useMemo(() => {
    const templates = [
      ...(skosOptions.conceptSchemeFilter ?? []),
      ...(skosOptions.conceptFilter ?? []),
    ];
    return templates.length > 0 ? templates : undefined;
  }, [skosOptions.conceptSchemeFilter, skosOptions.conceptFilter]);

  const { isReady } = useLoaderNunjucks(templatesToLoad);

  const resolvedConceptSchemeFilter = useMemo(() => {
    if (skosOptions.conceptSchemeFilter === undefined) return undefined;
    if (!isReady) return { values: [], errors: [] };
    return resolveSkosFilterEntries(
      nunjucksApi,
      formContext,
      skosOptions.conceptSchemeFilter,
    );
  }, [skosOptions.conceptSchemeFilter, nunjucksApi, formContext, isReady]);

  const resolvedConceptFilter = useMemo(() => {
    if (skosOptions.conceptFilter === undefined) return undefined;
    if (!isReady) return { values: [], errors: [] };
    return resolveSkosFilterEntries(
      nunjucksApi,
      formContext,
      skosOptions.conceptFilter,
    );
  }, [skosOptions.conceptFilter, nunjucksApi, formContext, isReady]);

  const filterErrors = useMemo(
    () => [
      ...(resolvedConceptSchemeFilter?.errors ?? []),
      ...(resolvedConceptFilter?.errors ?? []),
    ],
    [resolvedConceptSchemeFilter, resolvedConceptFilter],
  );

  // While templates are still loading or a filter expression failed to
  // render, show no results instead of falling back to the "no filter
  // configured" default (all concept schemes) — the field will also be
  // disabled by EntitySearchPicker once the error propagates.
  const blockResults = !isReady || filterErrors.length > 0;

  const rdfProps = useMemo(
    () =>
      translateSkosToRdf({
        ...skosOptions,
        conceptSchemeFilter: resolvedConceptSchemeFilter?.values,
        conceptFilter: resolvedConceptFilter?.values,
      }),
    [skosOptions, resolvedConceptSchemeFilter, resolvedConceptFilter],
  );

  const rdfPropsKey = useMemo(
    () => JSON.stringify({ rdfProps, blockResults }),
    [rdfProps, blockResults],
  );

  const provider = useMemo(() => {
    const searchTreeParams: Omit<NodesResolveRequest, 'offset' | 'limit'> = {
      startNode: blockResults
        ? [[{ predicate: '@id', in: [] }]]
        : rdfProps.startNode,
      traverse: rdfProps.traverse,
      return: rdfProps.returnOptions,
      prefixMap: rdfProps.prefixMap,
    };
    return new RdfConceptsHandlerProvider({
      kgmApi,
      searchTreeParams,
      labelProperty: rdfProps.labelProperty,
      expandStartNodes: rdfProps.expandStartNodes,
      childrenPageSize: rdfProps.childrenPageSize,
      schemeExpansion: rdfProps.schemeExpansion,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kgmApi, rdfPropsKey]);

  const effectiveKind: TemplateEntity = {
    ...templateKind,
    columns: templateKind.columns ?? defaultColumns,
  };

  return provider.renderContextProvider(
    effectiveKind,
    <>
      {filterErrors.length > 0 && (
        <WbAlert
          message={{
            type: 'error',
            title: 'Invalid conceptSchemeFilter/conceptFilter expression(s)',
            content: `Failed to render the following expression(s): ${filterErrors
              .map(entry => `'${entry}'`)
              .join(', ')}. Check that the expression syntax is valid.`,
          }}
        />
      )}
      {children}
    </>,
  );
};

/**
 * EntitySearchPicker provider for SKOS business concepts browsed from the
 * Knowledge Graph Manager.
 *
 * Template authors use `type: BusinessConcept` in their entity list and set
 * SKOS-specific options at the same level:
 * - `conceptSchemeFilter` / `conceptFilter`
 * - `displayField`: RDF predicate used as the display label (default: `skos:prefLabel`)
 * - `expandStartNodes`: when true, shows the first-level children of the start
 *   nodes instead of the start nodes themselves (default: false)
 * - `childrenPageSize`: number of children fetched per page when expanding or
 *   paginating a tree node (default: 20)
 * - `columns`
 * - `returnField`: only `iri` and `full` are supported. `iri` (the default when
 *   omitted) returns the RDF/SKOS IRI of the concept as a string; `full` returns
 *   the full `GenericEntityType` object with properties accessible via
 *   `entity.properties['skos:prefLabel']`. `urn` and `ref` are **not** supported
 *   for this provider. When set, `configError` is populated instead of throwing,
 *   so the picker field can be disabled with an inline error message.
 *
 * Internally delegates all provider logic to `RdfConceptsHandlerProvider`,
 * passing the SKOS-derived `NodesResolveRequest` parameters computed once at
 * construction time.
 */
export class BusinessConceptsHandlerProvider
  implements Provider<GenericEntityType[], ProviderFilters>
{
  private readonly rdfProvider?: RdfConceptsHandlerProvider;
  private readonly kgmApi: KnowledgeGraphManagerApi;
  private readonly nunjucksApi: WbSafeNunjucksApi;
  /**
   * Raw (unresolved) SKOS options extracted from the template, kept around so
   * `renderContextProvider` can re-resolve Nunjucks filter expressions
   * against `formContext` on every render. Undefined when the provider is
   * misconfigured (see `configError`).
   */
  private readonly skosOptions?: SkosOptions;
  /**
   * True when `conceptSchemeFilter` or `conceptFilter` contains a Nunjucks
   * expression (`{{ ... }}`). When true, filters are resolved
   * at render time via `DynamicSkosFilterResolver` instead of once at
   * construction time.
   */
  private readonly hasNunjucksFilters: boolean = false;
  /**
   * Default columns derived from the template config (including SKOS
   * defaults such as the skos:prefLabel column). Injected into templateKind
   * at render time when the template does not specify explicit columns.
   */
  private readonly defaultColumns: TemplateColumn[] = [SKOS_DEFAULT_COLUMN];

  /**
   * Set when the template configuration is invalid (e.g. an unsupported
   * `returnField`). Consumers (EntitySearchPicker) check this flag to
   * disable the field and show an inline error instead of crashing the form.
   */
  readonly configError?: string;

  constructor(
    kgmApi: KnowledgeGraphManagerApi,
    availableKinds: TemplateEntity[],
    nunjucksApi: WbSafeNunjucksApi,
  ) {
    this.kgmApi = kgmApi;
    this.nunjucksApi = nunjucksApi;

    const templateKind =
      selectTemplateEntity(availableKinds, BUSINESS_CONCEPT_TYPE) ??
      ({ type: BUSINESS_CONCEPT_TYPE } as TemplateEntity);

    if (
      templateKind.returnField === ReturnFieldEnum.urn ||
      templateKind.returnField === ReturnFieldEnum.ref
    ) {
      this.configError = `BusinessConcept provider does not support returnField '${templateKind.returnField}'. Use 'iri' or 'full'.`;
      this.filtersSection = () => <></>;
      return;
    }
    if (!templateKind.returnField) {
      templateKind.returnField = ReturnFieldEnum.iri;
    }

    const skosOptions = extractSkosOptions(templateKind);

    const nonStringFilterEntry = [
      ...(skosOptions.conceptSchemeFilter ?? []),
      ...(skosOptions.conceptFilter ?? []),
    ].find(entry => typeof entry !== 'string');
    if (nonStringFilterEntry !== undefined) {
      this.configError =
        `conceptSchemeFilter/conceptFilter entries must be strings, but found ${JSON.stringify(
          nonStringFilterEntry,
        )}. This can happen when a Nunjucks expression such as "{{ ... }}" ` +
        `is written without surrounding quotes in YAML, which makes it parse ` +
        `as a mapping instead of text. Wrap it in quotes (e.g. "{{ ... }}").`;
      this.filtersSection = () => <></>;
      return;
    }

    this.skosOptions = skosOptions;
    this.hasNunjucksFilters =
      (skosOptions.conceptSchemeFilter ?? []).some(
        containsNunjucksExpression,
      ) || (skosOptions.conceptFilter ?? []).some(containsNunjucksExpression);

    // When filters use Nunjucks expressions, they can only be resolved at
    // render time against `formContext` (see `DynamicSkosFilterResolver`).
    // The static rdfProvider built here is only used as a fallback for
    // adapt()/resolve()/extractEntityId()/fetchAncestors(), none of which
    // depend on the seed startNode, so "all concept schemes" is a safe default.
    const staticSkosOptions: SkosOptions = this.hasNunjucksFilters
      ? {
          ...skosOptions,
          conceptSchemeFilter: undefined,
          conceptFilter: undefined,
        }
      : skosOptions;

    const rdfProps = translateSkosToRdf(staticSkosOptions);

    const searchTreeParams: Omit<NodesResolveRequest, 'offset' | 'limit'> = {
      startNode: rdfProps.startNode,
      traverse: rdfProps.traverse,
      return: rdfProps.returnOptions,
      prefixMap: rdfProps.prefixMap,
    };

    this.defaultColumns = rdfProps.columns ?? [SKOS_DEFAULT_COLUMN];
    this.rdfProvider = new RdfConceptsHandlerProvider({
      kgmApi,
      searchTreeParams,
      labelProperty: rdfProps.labelProperty,
      expandStartNodes: rdfProps.expandStartNodes,
      childrenPageSize: rdfProps.childrenPageSize,
      schemeExpansion: rdfProps.schemeExpansion,
    });
    this.filtersSection = this.rdfProvider.filtersSection;
  }

  // Delegate hook reference so WbGeneralPurposeEntityPicker reads from the
  // same context that RdfConceptsStateProvider provides.
  useContext = useRdfConceptsContext;

  filtersSection: Provider<
    GenericEntityType[],
    ProviderFilters
  >['filtersSection'];

  renderContextProvider(
    templateKind: TemplateEntity | undefined,
    children: ReactNode,
    _parentTemplateRef?: string,
    formContext?: any,
  ): JSX.Element {
    if (!this.rdfProvider) {
      return <>{children}</>;
    }
    // Ensure userFilters and a sensible default column set are present even
    // when the template author omits them.
    const effectiveKind: TemplateEntity = {
      ...(templateKind ?? { type: BUSINESS_CONCEPT_TYPE }),
      userFilters: templateKind?.userFilters ?? ['search'],
      columns: templateKind?.columns ?? this.defaultColumns,
    };

    if (this.hasNunjucksFilters && this.skosOptions) {
      return (
        <DynamicSkosFilterResolver
          kgmApi={this.kgmApi}
          nunjucksApi={this.nunjucksApi}
          skosOptions={this.skosOptions}
          formContext={formContext}
          templateKind={effectiveKind}
          defaultColumns={this.defaultColumns}
        >
          {children}
        </DynamicSkosFilterResolver>
      );
    }

    return this.rdfProvider.renderContextProvider(effectiveKind, children);
  }

  adapt(entity: GenericEntityType): AdaptedEntity {
    if (!this.rdfProvider) {
      throw new Error(
        'BusinessConcept provider is misconfigured and cannot adapt entities.',
      );
    }
    return this.rdfProvider.adapt(entity);
  }

  async resolve(ref: string): Promise<TextFieldValue | undefined> {
    if (!this.rdfProvider) {
      return undefined;
    }
    return this.rdfProvider.resolve(ref);
  }

  extractEntityId(entity: GenericEntityType): string | undefined {
    return this.rdfProvider?.extractEntityId?.(entity);
  }

  async fetchAncestors(ref: string): Promise<string[] | undefined> {
    if (!this.rdfProvider) {
      return undefined;
    }
    return this.rdfProvider.fetchAncestors(ref);
  }
}
